import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import Logs from '@/components/common/Logs';
import ViewDocumentModal from '@/components/common/ViewDocumentModal';
import DocumentIcon from '@/components/Icons/DocumentIcon';
import Layout from '@/components/Layout/Layout';
import FullPageLoader from '@/components/loader/FullPageLoader';
import ApproveModal from '@/components/Modal/ApproveModal';
import BackButton from '@/components/ui/BackButton';
import { IDeleteResponse } from '@/interfaces/common.model';
import { IUser } from '@/interfaces/user.model';
import {
  calculateDaysDifference,
  handleStatus,
} from '@/utility/CommonFunctions';
import { capitalizeFirstLetter, showToastNotification } from '@/utility/helper';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
export interface ILeaveDetail {
  id: string;
  user: IUser;
  status: number;
  createdAt: string;
  startDate: string;
  endDate: string;
  leaveType: string;
  reason: string;
  documents: IDocument[];
  leaveDuration: string;
  url: string;
}
interface IDocument {
  name: string;
  url: string;
}
export interface ILeaveDetailResponse {
  status: boolean;
  message: string;
  data: ILeaveDetail;
}
interface ISubmitData {
  status: number | undefined;
  type: string;
  id: string | undefined;
  reason?: string;
}

const LeaveDetail = () => {
  const location = useLocation();
  const { id } = useParams();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [refreshLogs, setRefreshLogs] = useState<boolean>(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(
    null,
  );

  const [selectedDocument, setSelectedDocument] = useState<{
    url: string;
  } | null>(null);
  const [modal, setModal] = useState(false);
  const toggleModal = () => {
    setModal(!modal);
  };
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [leaveDetail, setLeaveDetail] = useState<ILeaveDetail | null>(null);

  useEffect(() => {
    GetLeaveDetail();
  }, []);

  const handleDocumentClick = (item: string) => {
    const docUrl = {
      url: item,
    };
    setSelectedDocument(docUrl);
    setModal(true);
  };

  const GetLeaveDetail = async () => {
    try {
      const API = API_URL + API_ENDPOINT?.getLeaveDetail + `?leaveId=${id}`;
      setDataLoading(true);
      const response: ILeaveDetailResponse = await callApi(API, {
        method: 'GET',
      });
      setDataLoading(false);
      if (response?.status === true) {
        setLeaveDetail(response?.data);
      } else {
        showToastNotification('danger', response?.message);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setDataLoading(false);
    }
  };
  const handleApproveReject = async (type: string, reason?: string) => {
    setIsLoading(true);
    const isHalfDay =
      leaveDetail?.leaveDuration == 'firstHalf' ||
      leaveDetail?.leaveDuration == 'secondHalf';
    let newStatus;
    if (type == 'approve') {
      newStatus = 1;
    } else if (type == 'reject') {
      newStatus = 2;
    }
    let submitData: ISubmitData = {
      type: 'leave',
      id: leaveDetail?.id,
      ...(isHalfDay ? { isHalfDayStatus: newStatus } : { status: newStatus }),
      status: newStatus,
    };
    if (reason) {
      submitData = {
        type: 'leave',
        id: leaveDetail?.id,
        status: newStatus,
        reason: reason,
      };
    }
    try {
      const response: IDeleteResponse = await callApi(
        API_URL + API_ENDPOINT.userStatusUpdate,
        {
          method: 'POST',
          data: submitData,
        },
      );
      if (response?.status) {
        setIsConfirmModalOpen(false);
        showToastNotification('success', response?.message);
        GetLeaveDetail();
        setRefreshLogs(true);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const submitConfirm = (actionType?: string, reason?: string) => {
    if (actionType === 'approve') {
      handleApproveReject('approve');
    } else if (actionType === 'reject') {
      handleApproveReject('reject', reason);
    }
  };

  return (
    <Layout>
      {dataLoading ? (
        <FullPageLoader />
      ) : (
        <>
          <div className="content container-fluid">
            <BackButton isDetail={true} label="Leave Detail" />
            <div className="card manage-user-list">
              <div className="card-body">
                <ul
                  className={
                    dataLoading
                      ? 'row list-unstyled mt-4 loading-skeleton'
                      : 'row list-unstyled mt-4'
                  }
                >
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Emp. Name :-
                      </span>
                      {capitalizeFirstLetter(leaveDetail?.user?.name ?? '--')}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Emp. Code :-
                      </span>
                      {leaveDetail?.user?.employeeCode || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Status :-</span>
                      {handleStatus(leaveDetail?.status ?? 0) || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Emp. Dept. :-
                      </span>
                      {leaveDetail?.user?.department?.name || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Emp. Designation :-
                      </span>
                      {leaveDetail?.user?.designation?.name || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Office :-</span>
                      {leaveDetail?.user?.site?.name || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Applied On :-
                      </span>
                      {(leaveDetail?.createdAt &&
                        moment(leaveDetail?.createdAt).format('DD/MM/YYYY')) ||
                        '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">From :-</span>
                      {(leaveDetail?.startDate &&
                        moment(leaveDetail?.startDate).format('DD/MM/YYYY')) ||
                        '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">To :-</span>
                      {(leaveDetail?.endDate &&
                        moment(leaveDetail?.endDate).format('DD/MM/YYYY')) ||
                        '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Days :-</span>
                      {leaveDetail?.startDate && leaveDetail?.endDate
                        ? (() => {
                            const days =
                              Number(
                                calculateDaysDifference(
                                  leaveDetail?.startDate,
                                  leaveDetail?.endDate,
                                  'number',
                                ),
                              ) || 0;
                            const result =
                              leaveDetail?.leaveDuration == 'firstHalf' ||
                              leaveDetail?.leaveDuration == 'secondHalf'
                                ? days * 0.5
                                : days;
                            return result
                              ? `${result} ${result === 1 ? 'day' : 'days'}`
                              : '--';
                          })()
                        : '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Leave Type :-
                      </span>
                      {leaveDetail?.leaveType || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Leave Duration :-
                      </span>
                      {leaveDetail?.leaveDuration
                        ? leaveDetail?.leaveDuration.includes('Half')
                          ? capitalizeFirstLetter(
                              leaveDetail?.leaveDuration,
                            )?.split('Half')?.[0] +
                            ' ' +
                            'Half'
                          : leaveDetail?.leaveDuration
                        : '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Leave Reason :-
                      </span>
                      {leaveDetail?.reason || '--'}
                    </p>
                  </li>
                </ul>
                {(location?.state?.type === 'request' ||
                  location?.state?.type === 'teamLeaveRequest') &&
                  leaveDetail?.status == 0 && (
                    <div className="card-body d-flex justify-content-end">
                      <div className="col-12 col-md-6 col-lg-4 ">
                        <span className="d-flex align-items-center gap-3 justify-content-end">
                          <>
                            <button
                              className="btn btn-success"
                              onClick={() => {
                                setActionType('approve');
                                setIsConfirmModalOpen(true);
                              }}
                            >
                              Approve
                            </button>
                            <button
                              className="btn btn-danger"
                              onClick={() => {
                                setActionType('reject');
                                setIsConfirmModalOpen(true);
                              }}
                            >
                              Reject
                            </button>
                          </>
                        </span>
                      </div>
                    </div>
                  )}
              </div>
            </div>
            <div className="row">
              <div className="col-12 ">
                <div className="card manage-user-list">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between border-bottom mb-3">
                      <h4 className="d-flex align-items-center mb-4">
                        Documents
                      </h4>
                    </div>
                    <ul className="row list-unstyled gap-3">
                      {leaveDetail?.url && leaveDetail?.url ? (
                        <li className="col-12">
                          <div
                            className="text-muted fs-4 d-flex gap-2 text-decoration-none"
                            onClick={() =>
                              handleDocumentClick(leaveDetail?.url)
                            }
                            role="button"
                          >
                            <DocumentIcon />
                            {leaveDetail?.url?.split('-')[1] || 'Document'}
                          </div>
                        </li>
                      ) : (
                        <div>No Document Found</div>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Logs
            type={'leave'}
            id={id ?? ''}
            refreshLogs={refreshLogs}
            setRefreshLogs={setRefreshLogs}
          />
          <ViewDocumentModal
            isOpen={modal}
            toggle={toggleModal}
            selectedDocument={selectedDocument}
          />
          <ApproveModal
            isOpen={isConfirmModalOpen}
            isLoading={isLoading}
            submitHandler={submitConfirm}
            toggle={() => setIsConfirmModalOpen(false)}
            actionType={actionType ?? ''}
          />
        </>
      )}
    </Layout>
  );
};

export default LeaveDetail;
